import { useTranslation } from '@shared/core';
import globalWindow from '@shared/core/globals';
import { animationTransition } from '@shared/utils/animationTransition';
import { useFeedBackSurvey } from '@shared/utils/feedbackSurvey';
import { Box, Flex, Menu, TextV2, useDisclosure } from '@withjoy/joykit';
import { Tip, Help, NotificationBubble } from '@withjoy/joykit/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import React, { useCallback } from 'react';
import { ComponentWithAs } from '@shared/utils/forwardRef';
import { useOuterClick } from '@shared/utils/hooks/useOuterClick';

type HelpMenuItemProps = {
  icon: React.ReactNode;
  text: string;
  onClick?: () => void;
};

export const HelpMenuItem: ComponentWithAs<'div', HelpMenuItemProps> = ({ icon, text, ...restProps }) => {
  return (
    <Menu.Item
      overrides={{
        ListItem: {
          props: {
            padding: 0,
            minHeight: 0
          }
        }
      }}
      content={
        <Flex
          alignItems={'center'}
          paddingX={5}
          height={pxToRem(48)}
          columnGap={4}
          borderRadius={2}
          backgroundColor={'white'}
          _hover={{ backgroundColor: 'mono2' }}
          _active={{ backgroundColor: 'mono3' }}
          transition={animationTransition('background-color')}
          {...restProps}
        >
          {icon} <TextV2>{text}</TextV2>
        </Flex>
      }
    />
  );
};

export const HeaderHelpMenu: React.FC<{
  renderButton: (toggleMenu: () => void, isOpen: boolean) => React.ReactNode;
  renderExtraMenuItems?: React.ReactNode;
  onOpenMenu?: () => void;
  onCloseMenu?: () => void;
  onClickSuggestion?: () => void;
  onClickSupport?: () => void;
  onClickHelp?: () => void;
}> = ({ renderButton, renderExtraMenuItems, onOpenMenu, onCloseMenu, onClickSuggestion, onClickSupport, onClickHelp }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const startSurvey = useFeedBackSurvey();
  const { t2 } = useTranslation('headerHelpMenu');
  const { help, suggestion, support } = t2('menuItems');

  const handleOpen = useCallback(() => {
    onOpenMenu?.();
    onOpen();
  }, [onOpen, onOpenMenu]);

  const handleClose = useCallback(() => {
    onCloseMenu?.();
    onClose();
  }, [onClose, onCloseMenu]);

  const toggleMenu = useCallback(() => {
    isOpen ? handleClose() : handleOpen();
  }, [handleClose, handleOpen, isOpen]);

  const menuRef = useOuterClick(toggleMenu, isOpen);

  const handleSuggestion = useCallback(() => {
    onClickSuggestion?.();
    startSurvey();
    onClose();
  }, [onClickSuggestion, onClose, startSurvey]);

  const handleSupport = useCallback(() => {
    const Intercom = globalWindow.Intercom;
    if (Intercom) {
      onClickSupport?.();
      Intercom('show');
    }
    onClose();
  }, [onClickSupport, onClose]);

  const handleHelp = useCallback(() => {
    onClickHelp?.();
    onClose();
  }, [onClickHelp, onClose]);

  return (
    <Box position={'relative'} ref={menuRef}>
      {renderButton(toggleMenu, isOpen)}
      <Box
        __css={{
          opacity: isOpen ? 1 : 0,
          transition: animationTransition('all'),
          transitionDuration: '0.3s',
          transformOrigin: 'right top',
          transform: isOpen ? 'scaleY(1) scaleX(1)' : 'scaleY(0.9) scaleX(0.9)',
          visibility: isOpen ? 'visible' : 'hidden',
          position: 'absolute',
          right: 0,
          zIndex: 'dropdown',
          marginTop: 4
        }}
      >
        <Menu
          overrides={{
            List: {
              props: {
                padding: 6,
                rowGap: 1,
                as: Flex,
                flexDirection: 'column',
                borderRadius: pxToRem(12),
                boxShadow: '0px 7px 27px 0px rgba(44, 41, 37, 0.06), 0px 4px 16px 0px rgba(0, 0, 0, 0.07)'
              }
            }
          }}
        >
          <HelpMenuItem icon={<Tip />} text={suggestion} onClick={handleSuggestion} />
          <HelpMenuItem icon={<NotificationBubble />} text={support} onClick={handleSupport} />
          <HelpMenuItem icon={<Help />} text={help} onClick={handleHelp} as="a" href="https://help.withjoy.com/" target="_blank" />
          {renderExtraMenuItems}
        </Menu>
      </Box>
    </Box>
  );
};
